import { yupResolver } from '@hookform/resolvers/yup'
import { IconChevronLeft, IconInfoCircle } from '@tabler/icons'
import { AxiosResponse } from 'axios'
import classNames from 'classnames'
import { cloneDeep, debounce, isEqual } from 'lodash'
import Plausible from 'plausible-tracker'
import { useEffect, useRef, useState } from 'react'
import React from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Group, Tooltip } from '@mantine/core'
import { Alert, Button, ContentCard } from '@/components/Elements'
import { BookingSubmissionModal } from '@/components/Elements/Modals/BookingModal'
import { useAppState, useEvents } from '@/features/app/hooks'
import { useAuth } from '@/features/auth'
import useStyles from '@/features/booking/components/Booking/BookingContainer/BookingWizardForm/BookingWizardForm.styles'
import {
  ageMean,
  ageMeanAnimation,
  mapSubmitValuesCareDetails,
} from '@/features/booking/components/Booking/BookingContainer/BookingWizardForm/helpers'
import { getValidationSchema } from '@/features/booking/components/Booking/BookingContainer/BookingWizardForm/validation'
import PriceTooltip from '@/features/booking/helpers/priceTooltip/PriceTooltip'
import { bookingCreatePA } from '@/features/booking/store'
import { checkoutState } from '@/features/booking/store/checkout.slice'
import { bookingCreateNoLoginPA } from '@/features/booking/store/createNoLogin/saga'
import { previousRequestState } from '@/features/booking/store/previousRequest.slice'
import { bookingAPI, bookingsAPI } from '@/features/bookings/api'
import { BOOKING_TYPE } from '@/features/bookings/consts/booking'
import { useBookingOptions } from '@/features/bookings/hooks'
import { useFormSubmit, useLang, useNotify, usePopup } from '@/hooks'
import { BookingWizardFormBanner } from '../../BookingWizardFormBanner'
import { ChildrenFormSection, InfoFormSection } from './FormSections'

interface IProps {
  initialValuesDetails: { general: object }
  initialValuesBooking: { general: object; timetable: object }
  bookingType: BOOKING_TYPE
  frame: boolean
}

export const CareDetailsForm = ({
  initialValuesDetails,
  initialValuesBooking,
  bookingType,
  frame,
}: IProps) => {
  const { classes } = useStyles()
  const { showNotification } = useNotify()
  const dispatch = useDispatch()
  const { visible, open, close } = usePopup()
  const {
    appState: { mobileView },
  } = useAppState()

  const { t } = useTranslation()
  const { id } = useParams()
  const { isLoggedIn } = useAuth()
  const { lang } = useLang()
  const { getType } = useBookingOptions()
  const navigate = useNavigate()
  const events = useEvents()
  const { booking } = useSelector((state: any) => state.booking.checkout)
  const viewport = useRef<HTMLDivElement>()
  const canChangePageRef = useRef<boolean>(false)
  const [calculatedPrice, setCalculatedPrice] = useState<number>(0)
  const [priceAnalysis, setPriceAnalysis] = useState<any>({})
  const [calculatingPrice, setCalculatingPrice] = useState<boolean>(false)
  const skipNextCalcRef = useRef<boolean>(false)
  const previousPriceRequestRef = useRef<any>({})
  const { trackEvent } = Plausible({
    domain: 'babysisters.pt',
  })
  const scrollToTop = () => {
    if (viewport?.current) {
      viewport.current.scrollTo({ top: 0 })
    } else {
      window.scrollTo(0, 0)
    }
  }

  useEffect(() => {
    scrollToTop()
    if (!visible) {
      debouncedCalcPrice()
      if (!booking || (frame && !booking?.type)) {
        if (frame) {
          if (id) {
            navigate('/booking/frame/edit/' + id)
          } else {
            navigate('/booking/frame/new')
          }
        } else navigate('/booking/' + getType(bookingType).path + '/new', { replace: true })
      } else {
        const alertPageChange = (e: any) => {
          if (!canChangePageRef.current) {
            e.preventDefault()
            e.returnValue = ''
            return
          }
        }

        if (!frame) {
          window.addEventListener('beforeunload', alertPageChange)

          const type = booking?.type
          trackEvent(
            type === BOOKING_TYPE.ONETIME || type === BOOKING_TYPE.REGULAR
              ? 'booking/care-details'
              : type + '/care-details'
          )

          if (booking.type == BOOKING_TYPE.PERMANENT_NANNY) {
            events.actions.submissionNanny({
              Booking: booking.id,
            })
          }
        }
        return () => {
          if (!frame) window.removeEventListener('beforeunload', alertPageChange)
        }
      }
    }
  }, [])

  const defaultValues =
    frame && id
      ? {
          ...booking,
        }
      : {
          booking_children: [],
          booking_animation_children_0_2: [],
          booking_animation_children_3_4: [],
          booking_animation_children_4: [],
          primary_language_id: lang == 'en' ? 2 : 1,
          secondary_language_id: null,
          has_special_needs: false,
          phone: null,
          address: null,
          contact_email: null,
          // special_needs: '', // hidden field, depend on 'has_special_needs'
          //other_info: '',
          care_expectations: '',

          ...initialValuesBooking?.general,
          ...initialValuesDetails?.general,
          ...booking,
        }
  if (booking?.type == BOOKING_TYPE.ANIMATION) {
    defaultValues.booking_children = []
  } else {
    defaultValues.booking_animation_children_0_2 = []
    defaultValues.booking_animation_children_3_4 = []
    defaultValues.booking_animation_children_4 = []
  }

  // console.log('booking:' + JSON.stringify(initialValuesBooking?.general))
  // console.log('details:' + JSON.stringify(initialValuesDetails?.general))
  // console.log('defaultValues:' + JSON.stringify(defaultValues))

  const [alertError, setAlertError] = useState<string | null>(null)

  const currentValidationSchema = getValidationSchema({
    step: 2,
    hasUser: isLoggedIn,
    type: booking?.type,
    isHotel: booking?.is_hotel,
    frame: frame,
  })

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(currentValidationSchema),
  })

  const {
    handleSubmit,
    trigger,
    setError,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = methods

  const watchCareDetails = watch([
    'booking_children',
    'booking_animation_children_0_2',
    'booking_animation_children_3_4',
    'booking_animation_children_4',
    'entertainment_kit',
    'extra_activities.balloon_modeling',
    'extra_activities.facial_painting',
    'extra_activities.hours',
  ])
  const watchForm = watch()

  useEffect(() => {
    if (!frame) {
      const values = {
        ...getValues(),
      }
      localStorage.setItem('working-booking', JSON.stringify(values))
    }
  }, [watchForm])

  const saveCareDetails = async (formData?: any): Promise<AxiosResponse> => {
    if (!frame) {
      const params = {
        ...booking,
        ...formData,
        //booking_id: booking?.id,
      }
      if (booking.type == BOOKING_TYPE.ANIMATION && !params.extra_activities) {
        params.extra_activities = {
          facial_painting: false,
          balloon_modeling: false,
        }
      }
      let response
      if (!isLoggedIn) {
        response = await bookingsAPI.createBookingNoLogin(params)
        await dispatch(
          checkoutState.setBooking({
            ...response.data.booking,
            booking_children: params.booking_children,
            booking_animation_children_0_2: params.booking_animation_children_0_2,
            booking_animation_children_3_4: params.booking_animation_children_3_4,
            booking_animation_children_4: params.booking_animation_children_4,
            isNewuser: response.data.isNewuser,
            user: { id: response.data.userId },
            price_analysis: undefined,
          })
        )
      } else {
        response = await bookingsAPI.createBooking(params)
        await dispatch(
          checkoutState.setBooking({
            ...response.data.booking,
            booking_children: params.booking_children,
            booking_animation_children_0_2: params.booking_animation_children_0_2,
            booking_animation_children_3_4: params.booking_animation_children_3_4,
            booking_animation_children_4: params.booking_animation_children_4,
            user: { id: response.data.userId },
            price_analysis: undefined,
          })
        )
      }

      await dispatch(previousRequestState.setBooking(null))

      localStorage.removeItem('working-booking')
      return response
    } else {
      const params = {
        ...booking,
        ...formData,
        ...(id && { id: id }),
      }

      if (booking.type == BOOKING_TYPE.ANIMATION && !params.extra_activities) {
        params.extra_activities = {
          facial_painting: false,
          balloon_modeling: false,
        }
      }

      let response
      if (!id) {
        response = await bookingsAPI.saveBookingAdmin(params)
        await dispatch(
          checkoutState.setBooking({
            ...params,
            id: response.data.id,
            isNewuser: response.data.isNewuser,
            user: { id: response.data.userId },
            adminEdited: false,
          })
        )
      } else {
        response = await bookingsAPI.editBookingAdmin(params)
        await dispatch(
          checkoutState.setBooking({
            ...params,
            id: id,
            adminEdited: true,
          })
        )
      }
      return response
    }
  }

  const onSubmitCareDetails = async (values: any) => {
    try {
      setAlertError(null)

      const response: AxiosResponse = await saveCareDetails(values)

      canChangePageRef.current = true
      if (!frame) {
        if (!isLoggedIn) {
          if (response.data.isNewuser) {
            events.actions.register({
              Email: values.contact_email,
              Type: 'Auto - App',
            })
          }
        }
        console.log('save care details')
        events.actions.careDetails({
          Type: booking.type,
          Value: response.data.total_amount,
          Booking: booking.id,
          AgeMean:
            booking.type === BOOKING_TYPE.ANIMATION
              ? ageMeanAnimation(
                  booking.booking_animation_children_0_2,
                  booking.booking_animation_children_3_4,
                  booking.booking_animation_children_4
                )
              : ageMean(booking.booking_children),
        })
        if (booking?.type === BOOKING_TYPE.PERMANENT_NANNY) {
          open(true)
        } else {
          navigate('/booking/' + getType(booking.type).path + '/checkout', { replace: true })
        }
      } else {
        navigate('/booking/frame/confirmed/', { replace: true })
      }
    } catch (e) {
      console.log('error:', e)
      console.error(e)
      setAlertError(t('booking_save_error'))
      showNotification({
        type: 'error',
        message: t('booking_save_error'),
      })
    }
  }

  const { error: submitError, onSubmit: onFormSubmit } = useFormSubmit({
    submit: onSubmitCareDetails,
    setError,
  })

  const onError = (errors: any, e: any) => {
    // console.log('Validation failed:')
    // console.log(errors) // Logs all validation errors
    // console.log('Invalid fields:', Object.keys(errors)) // Logs the paths of invalid fields
    const message = errors?.response?.data?.message || t('please_fill_all_required_fields')

    showNotification({
      type: 'error',
      message: message,
    })
  }

  const onSubmit: SubmitHandler<any> = async (data) => {
    const isStepValid = await trigger()
    if (isStepValid) {
      try {
        const values = {
          ...mapSubmitValuesCareDetails(data),
        }
        await onFormSubmit(values)
        // eslint-disable-next-line
      } catch (err) {
        showNotification({
          type: 'error',
          message: t('error_submitting_request'),
        })
      }
    } else {
      showNotification({
        type: 'error',
        message: t('please_fill_all_required_fields'),
      })
    }
  }
  const renderFooter = () => {
    return (
      <div className={'flex justify-between items-center'}>
        <div></div>

        <Group>
          <Button
            disabled={isSubmitting}
            variant="default"
            color="dark"
            leftIcon={<IconChevronLeft />}
            onClick={handleBack}
          >
            {t('back')}
          </Button>
          <Button loading={isSubmitting} onClick={handleSubmit(onSubmit, onError)}>
            {t(isLoggedIn ? 'save' : 'booking.continue')}
          </Button>
        </Group>
      </div>
    )
  }

  const renderTooltip = () => {
    return <PriceTooltip priceAnalysis={priceAnalysis} />
  }

  const renderFooterAdmin = () => {
    return (
      <div
        className={classNames(
          classes.footerPriceMobile,
          'flex',
          'justify-between',
          'items-center',
          {
            'sticky-footer': mobileView,
            'z-index': mobileView,
          }
        )}
      >
        {booking?.type === BOOKING_TYPE.PERMANENT_NANNY && <div></div>}
        {mobileView && booking?.type !== BOOKING_TYPE.PERMANENT_NANNY && (
          <div className={'flex flex-col'}>
            <Tooltip
              label={renderTooltip()}
              position="top"
              //withArrow
              //arrowSize={6}
              multiline
              events={{ hover: true, touch: true, focus: false }}
              disabled={calculatedPrice < 0.01}
            >
              <h2 className="flex gap-4 items-center cursor-pointer">
                <span className={classes.price}>
                  {t(booking?.type === BOOKING_TYPE.REGULAR ? 'price.1st_month' : 'price')}
                </span>
                <i className="pi pi-info-circle"></i>
              </h2>
            </Tooltip>
            <h2 className="flex gap-4 items-center cursor-pointer" style={{ height: '30px' }}>
              {!calculatingPrice ? (
                <span>€{calculatedPrice?.toFixed(2)}</span>
              ) : (
                <i className="pi pi-spin pi-spinner"></i>
              )}
            </h2>
          </div>
        )}

        {!mobileView && booking?.type != BOOKING_TYPE.PERMANENT_NANNY && (
          <Tooltip
            label={renderTooltip()}
            position="top"
            withArrow
            //arrowSize={6}
            multiline
            events={{ hover: true, touch: true, focus: false }}
            disabled={calculatedPrice < 0.01}
          >
            <h2 className="flex gap-4 items-center cursor-pointer">
              <span>
                {t(booking?.type === BOOKING_TYPE.REGULAR ? 'price.1st_month' : 'price')}:{' '}
                {!calculatingPrice ? (
                  <>{calculatedPrice?.toFixed(2)}€</>
                ) : (
                  <i className="pi pi-spin pi-spinner"></i>
                )}
              </span>
              <i className="pi pi-info-circle"></i>
            </h2>
          </Tooltip>
        )}

        <Group>
          <Button
            disabled={isSubmitting}
            variant="default"
            color="dark"
            leftIcon={<IconChevronLeft />}
            onClick={handleBack}
          >
            {t('back')}
          </Button>
          <Button loading={isSubmitting} onClick={handleSubmit(onSubmit, onError)}>
            {t(isLoggedIn ? 'save' : 'booking.continue')}
          </Button>
        </Group>
      </div>
    )
  }

  const handleBack = () => {
    //if (frame) {
    navigate(-1)
    //}
  }
  const getCalculatedPrice = async () => {
    console.log('getCalculatedPrice')
    setCalculatingPrice(true)
    if (booking.type !== BOOKING_TYPE.PERMANENT_NANNY) {
      const values = {
        ...mapSubmitValuesCareDetails(getValues()),
      }

      const params = {
        ...booking,
        ...values,
        ...(id && { id: id }),
      }

      if (booking.type == BOOKING_TYPE.ANIMATION && !params.extra_activities) {
        params.extra_activities = {
          facial_painting: false,
          balloon_modeling: false,
        }
      }
      const calculate = true

      params.booking_children = params.booking_children?.filter(
        (obj: any) => obj.name && obj.birthday
      )
      params.booking_animation_children_0_2 = params.booking_animation_children_0_2?.filter(
        (obj: any) => obj.name && obj.kid_age
      )
      params.booking_animation_children_3_4 = params.booking_animation_children_3_4?.filter(
        (obj: any) => obj.name && obj.kid_age
      )
      params.booking_animation_children_4 = params.booking_animation_children_4?.filter(
        (obj: any) => obj.name && obj.kid_age
      )
      if (calculate && !isEqual(previousPriceRequestRef.current, params)) {
        previousPriceRequestRef.current = cloneDeep(params)

        try {
          const { data } = await bookingAPI.getCalculatedPrice(isLoggedIn, params)
          setCalculatedPrice(data.price)
          if (
            !priceAnalysis ||
            JSON.stringify(data.bookingTableInformations) !== JSON.stringify(priceAnalysis)
          ) {
            setPriceAnalysis(data.bookingTableInformations)
          }
          skipNextCalcRef.current = true
        } catch (error: any) {
          const message = error?.response?.data?.message || t('price_calc_error')
          showNotification({
            type: 'error',
            message: message,
          })

          setCalculatedPrice(0)
          setPriceAnalysis({})
          skipNextCalcRef.current = true
        }
      }
    }
    setCalculatingPrice(false)
  }

  const debouncedCalcPrice = React.useCallback(debounce(getCalculatedPrice, 500), [])

  useEffect(() => {
    if (skipNextCalcRef.current) {
      skipNextCalcRef.current = false
    } else {
      if (booking) debouncedCalcPrice()
    }
  }, [watchCareDetails])

  return (
    <>
      {booking && (
        <FormProvider {...methods}>
          <form className={mobileView ? '' : 'h-full'}>
            <ContentCard
              title={t('book.estimated_price_details')}
              footer={frame ? renderFooterAdmin() : renderFooter()}
              scrollable={!mobileView}
              fullHeight={!mobileView}
              viewport={viewport}
              className={classes.cardWrapper}
            >
              <div className={classes.wrapper}>
                {alertError && (
                  <Alert type={'error'} mb={'sm'}>
                    {alertError}
                  </Alert>
                )}
                {submitError && (
                  <Alert type={'error'} mb={'sm'}>
                    {submitError?.message || t('error')}
                  </Alert>
                )}
                {!frame && booking?.type != BOOKING_TYPE.PERMANENT_NANNY && (
                  <Group className={'w-100 gap-2 mb-4'} noWrap>
                    <div className={classNames(classes.tooltipClient, 'flex flex-col')}>
                      <Tooltip
                        label={renderTooltip()}
                        position="bottom"
                        //withArrow
                        //arrowSize={6}
                        multiline
                        events={{ hover: true, touch: true, focus: false }}
                        disabled={calculatedPrice < 0.01}
                      >
                        <h2 className="flex m-0 gap-1 text-center">
                          <span className={classes.price}>
                            {t(
                              booking?.type === BOOKING_TYPE.REGULAR
                                ? 'price.1st_month'
                                : 'book.estimated_price'
                            )}
                          </span>
                          <i className="pi pi-info-circle"></i>
                        </h2>
                      </Tooltip>
                      <h2 className="flex gap-4 text-center" style={{ height: '30px' }}>
                        {!calculatingPrice ? (
                          <span className={'w-100'}>€{calculatedPrice?.toFixed(2)}</span>
                        ) : (
                          <i className="pi pi-spin pi-spinner"></i>
                        )}
                      </h2>
                    </div>

                    <BookingWizardFormBanner
                      icon={<IconInfoCircle size={14} />}
                      text={t('book.service_details_disclaimer')}
                      color={'#eff6fe'}
                    />
                  </Group>
                )}
                <ChildrenFormSection
                  boldTitle={true}
                  animation={booking?.type == BOOKING_TYPE.ANIMATION}
                />

                <div className={mobileView ? 'mt-1' : 'mt-3'}>
                  <InfoFormSection frame={frame} />
                </div>
              </div>
              {alertError && (
                <Alert type={'error'} mb={'sm'}>
                  {alertError}
                </Alert>
              )}
            </ContentCard>
          </form>
        </FormProvider>
      )}
      <BookingSubmissionModal visible={visible} onModalClose={close} />
    </>
  )
}
